import { useEffect, useState } from "react";
import { parseISO } from "date-fns";
import { Banner, Button, Card, Flex, FullscreenTakeover, Grid, Step } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { BatchPeriod } from "~/graphql/supergraphEnums";
import {
  useUpdateEntityGlSyncSettingsMutation,
  useGetEntityGlSyncSettingsQuery,
} from "~/graphql/supergraphHooks";
import { type GetEntityGlSyncSettingsQuery } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { toISOFormat } from "~/utils/datetime";
import { getCapitalActivityLabel } from "../../types";
import { formatDate } from "../../utils/helpers";
import { columnDefs, gridOptions } from "./columnDefs";
import { SettingCard } from "./SettingCard";
import StepNavigationIndicator from "./StepNavigation";
import styles from "./UpdateTransactionTakeover/styles";

type Props = {
  onExit: () => void;
  entityName: string;
  entityId: number;
  arenaName: string;
  arenaId: any;
};

type EntityGLSyncSettings = NonNullable<GetEntityGlSyncSettingsQuery["entityGLSyncSettings"]>;
type CapitalActivitySettings = NonNullable<EntityGLSyncSettings["capitalCallSettings"]>;
type NetIncomeSettings = NonNullable<EntityGLSyncSettings["netIncomeSettings"]>;

enum STEP_NAMES {
  configureSettings = "Configure settings",
  reviewSettings = "Review",
}

export const ActiveGLIntegrationTakeover = ({
  onExit,
  arenaName,
  entityId,
  entityName,
  arenaId,
}: Props) => {
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const capitalActivityLabel = getCapitalActivityLabel(showCapitalActivityLabelCanopy);
  const { data: entityGlSyncSettingsData, loading: entityGlSyncSettingsLoading } =
    useGetEntityGlSyncSettingsQuery({
      variables: {
        arenaId,
        entityId,
      },

      fetchPolicy: "network-only",
    });

  const [errorMessage, setErrorMessage] = useState("");

  // Net Income
  const [glStartDateNetIncome, setGlStartDateNetIncome] = useState<Date | null>(null);
  const [useCustomStartDateNetIncome, setUseCustomStartDateNetIncome] = useState(false);
  const [batchPeriodNetIncome, setBatchPeriodNetIncome] = useState<`${BatchPeriod}`>(
    BatchPeriod.Quarterly,
  );
  const [checkedNetIncome, setCheckedNetIncome] = useState(true);

  // Capital Call
  const [glStartDateCapitalCalls, setGlStartDateCapitalCalls] = useState<Date | null>(null);
  const [useCustomStartDateCapitalCalls, setUseCustomStartDateCapitalCalls] = useState(false);
  const [checkedCapitalCalls, setCheckedCapitalCalls] = useState(true);

  // Distribution
  const [glStartDateDistributions, setGlStartDateDistributions] = useState<Date | null>(null);
  const [useCustomStartDateDistributions, setUseCustomStartDateDistributions] = useState(false);
  const [checkedDistributions, setCheckedDistributions] = useState(true);

  // Redemption
  const [glStartDateRedemptions, setGlStartDateRedemptions] = useState<Date | null>(null);
  const [useCustomStartDateRedemptions, setUseCustomStartDateRedemptions] = useState(false);
  const [checkedRedemptions, setCheckedRedemptions] = useState(true);

  // Transfers
  const [glStartDateTransfers, setGlStartDateTransfers] = useState<Date | null>(null);
  const [useCustomStartDateTransfers, setUseCustomStartDateTransfers] = useState(false);
  const [checkedTransfers, setCheckedTransfers] = useState(true);

  // Steps
  const [currentStep, setCurrentStep] = useState<STEP_NAMES>(STEP_NAMES.configureSettings);

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    if (!entityGlSyncSettingsLoading) {
      // Has any existing settings
      const hasExistingSettings: boolean =
        !!entityGlSyncSettingsData?.entityGLSyncSettings?.netIncomeSettings ||
        !!entityGlSyncSettingsData?.entityGLSyncSettings?.capitalCallSettings ||
        !!entityGlSyncSettingsData?.entityGLSyncSettings?.distributionSettings ||
        !!entityGlSyncSettingsData?.entityGLSyncSettings?.capitalTransactionSettings;

      // Set form values for each setting category with existing settings.

      // Net Income Settings
      setCheckedNetIncome(
        entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.active ??
          !hasExistingSettings,
      );
      setBatchPeriodNetIncome(
        entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.batchPeriod ??
          BatchPeriod.Quarterly,
      );

      // Capital Activity Settings
      setCheckedCapitalCalls(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings?.active ??
          !hasExistingSettings,
      );
      setCheckedDistributions(
        entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings?.active ??
          !hasExistingSettings,
      );
      setCheckedRedemptions(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings?.active ??
          !hasExistingSettings,
      );

      // Transfer Settings
      setCheckedTransfers(
        entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.active ??
          !hasExistingSettings,
      );

      // Use whether we want to use custom start date across each category
      setUseCustomStartDateNetIncome(
        entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
          ? entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings.active
            ? entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.glStartDate
              ? true
              : false
            : true
          : true,
      );
      setUseCustomStartDateCapitalCalls(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
          ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings.active
            ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings?.glStartDate
              ? true
              : false
            : true
          : true,
      );
      setUseCustomStartDateDistributions(
        entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
          ? entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings.active
            ? entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings?.glStartDate
              ? true
              : false
            : true
          : true,
      );
      setUseCustomStartDateRedemptions(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings
          ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings.active
            ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings
                ?.glStartDate
              ? true
              : false
            : true
          : true,
      );
      setUseCustomStartDateTransfers(
        entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings
          ? entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings.active
            ? entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.glStartDate
              ? true
              : false
            : true
          : true,
      );

      // Now set the actual custom gl_start date across each category (if opted-in by above section)
      setGlStartDateNetIncome(
        entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.glStartDate
          ? new Date(
              parseISO(
                entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.glStartDate,
              ),
            )
          : null,
      );
      setGlStartDateCapitalCalls(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings?.glStartDate
          ? new Date(
              parseISO(
                entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings?.glStartDate,
              ),
            )
          : null,
      );
      setGlStartDateDistributions(
        entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings?.glStartDate
          ? new Date(
              parseISO(
                entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                  ?.glStartDate,
              ),
            )
          : null,
      );
      setGlStartDateRedemptions(
        entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings?.glStartDate
          ? new Date(
              parseISO(
                entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings
                  ?.glStartDate,
              ),
            )
          : null,
      );
      setGlStartDateTransfers(
        entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.glStartDate
          ? new Date(
              parseISO(
                entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.glStartDate,
              ),
            )
          : null,
      );

      // Loading
      setSettingsLoaded(true);
    }
  }, [entityGlSyncSettingsData, entityGlSyncSettingsLoading]);

  const [updateSyncSettings, { loading: mutationLoading }] =
    useUpdateEntityGlSyncSettingsMutation({
      fetchPolicy: "network-only",
    });

  function nextStep() {
    setCurrentStep(STEP_NAMES.reviewSettings);
  }
  function onBack() {
    setCurrentStep(STEP_NAMES.configureSettings);
  }

  const steps: Step[] = [
    {
      name: STEP_NAMES.configureSettings,
      // url is used as key in the .map loop for steps in StepIndicator
      // it is required for React
      url: STEP_NAMES.configureSettings,
      status: currentStep == STEP_NAMES.configureSettings ? "active" : "complete",
      hidden: false,
      index: 1,
    },
    {
      name: STEP_NAMES.reviewSettings,
      url: STEP_NAMES.reviewSettings,
      status: currentStep == STEP_NAMES.reviewSettings ? "active" : "inactive",
      hidden: false,
      index: 2,
    },
  ];

  function onSubmit() {
    async function submit() {
      const response = await updateSyncSettings({
        variables: {
          input: {
            entityId,
            netIncomeSettings: {
              glStartDate: glStartDateNetIncome ? toISOFormat(glStartDateNetIncome) : null,
              batchPeriod: batchPeriodNetIncome,
              active: checkedNetIncome,
            },
            capitalActivitySettings: [
              {
                glStartDate: glStartDateCapitalCalls
                  ? toISOFormat(glStartDateCapitalCalls)
                  : null,
                active: checkedCapitalCalls,
                transactionCategory: "capital_call",
              },
              {
                glStartDate: glStartDateDistributions
                  ? toISOFormat(glStartDateDistributions)
                  : null,
                active: checkedDistributions,
                transactionCategory: "distribution",
              },
              {
                glStartDate: glStartDateRedemptions
                  ? toISOFormat(glStartDateRedemptions)
                  : null,
                active: checkedRedemptions,
                transactionCategory: "capital_transaction",
              },
            ],
            transferSettings: {
              glStartDate: glStartDateTransfers ? toISOFormat(glStartDateTransfers) : null,
              active: checkedTransfers,
              transactionCategory: "transfer",
            },
          },
        },
      });

      if (response.data?.updateEntityGLSyncSettings.success) {
        onExit();
      } else {
        setErrorMessage("Failed to update settings. Please try again.");
      }
    }

    submit().catch(() => {
      setErrorMessage("Failed to update settings. Please try again.");
    });
  }

  const title = `Configure integration settings  ·  ${entityName}`;

  function integrationStartDateChangedTo(
    checkedTransactionCategory: boolean,
    glTransactionCategoryStartDate: Date | null,
  ): string {
    return checkedTransactionCategory
      ? glTransactionCategoryStartDate
        ? formatDate(toISOFormat(glTransactionCategoryStartDate))
        : "Since inception"
      : "Integration disabled";
  }

  let summaryWidth = "600px";

  function integrationStartDateChangedFrom(
    settingsTransactionCategory: CapitalActivitySettings | NetIncomeSettings | null | undefined,
    checkedTransactionCategory: boolean,
    glTransactionCategoryStartDate: Date | null,
  ): string {
    const changedFrom =
      settingsTransactionCategory && !settingsTransactionCategory.active
        ? "Integration disabled"
        : settingsTransactionCategory?.glStartDate
          ? formatDate(toISOFormat(settingsTransactionCategory?.glStartDate))
          : settingsTransactionCategory
            ? "Since inception"
            : "";

    if (
      integrationStartDateChangedTo(
        checkedTransactionCategory,
        glTransactionCategoryStartDate,
      ) != changedFrom &&
      changedFrom != ""
    ) {
      summaryWidth = "800px";
    }
    return changedFrom;
  }

  const rowData = [
    {
      transactionType: "Capital calls",
      integrationStartDate: {
        changedTo: integrationStartDateChangedTo(checkedCapitalCalls, glStartDateCapitalCalls),
        changedFrom: integrationStartDateChangedFrom(
          entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings,
          checkedCapitalCalls,
          glStartDateCapitalCalls,
        ),
      },
      reportingInterval: {
        changedTo: "N/A",
        changedFrom: "",
      },
    },
    {
      transactionType: capitalActivityLabel,
      integrationStartDate: {
        changedTo: integrationStartDateChangedTo(checkedRedemptions, glStartDateRedemptions),
        changedFrom: integrationStartDateChangedFrom(
          entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings,
          checkedRedemptions,
          glStartDateRedemptions,
        ),
      },
      reportingInterval: {
        changedTo: "N/A",
        changedFrom: "",
      },
    },
    {
      transactionType: "Distributions",
      integrationStartDate: {
        changedTo: integrationStartDateChangedTo(
          checkedDistributions,
          glStartDateDistributions,
        ),
        changedFrom: integrationStartDateChangedFrom(
          entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings,
          checkedDistributions,
          glStartDateDistributions,
        ),
      },
      reportingInterval: {
        changedTo: "N/A",
        changedFrom: "",
      },
    },
    {
      transactionType: "Net Income",
      integrationStartDate: {
        changedTo: integrationStartDateChangedTo(checkedNetIncome, glStartDateNetIncome),
        changedFrom: integrationStartDateChangedFrom(
          entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings,
          checkedNetIncome,
          glStartDateNetIncome,
        ),
      },
      reportingInterval: {
        changedTo: checkedNetIncome
          ? batchPeriodNetIncome.charAt(0).toUpperCase() + batchPeriodNetIncome.slice(1)
          : "N/A",
        changedFrom: !checkedNetIncome
          ? ""
          : entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.batchPeriod
            ? entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.batchPeriod
                .charAt(0)
                .toUpperCase() +
              entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings?.batchPeriod.slice(
                1,
              )
            : "",
      },
    },
  ];

  rowData.push({
    transactionType: "Transfers",
    integrationStartDate: {
      changedTo: integrationStartDateChangedTo(checkedTransfers, glStartDateTransfers),
      changedFrom: integrationStartDateChangedFrom(
        entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings,
        checkedTransfers,
        glStartDateTransfers,
      ),
    },
    reportingInterval: {
      changedTo: "N/A",
      changedFrom: "",
    },
  });

  return (
    // style here make the takeover full screen
    <FullscreenTakeover css={styles.fullScreenTakeover}>
      <FullscreenTakeover.Header onExit={onExit} title={title} />
      <FullscreenTakeover.Subheader>
        <StepNavigationIndicator steps={steps} />
      </FullscreenTakeover.Subheader>
      <FullscreenTakeover.Body>
        {!settingsLoaded && <></>}
        {settingsLoaded && (
          <>
            {currentStep == STEP_NAMES.configureSettings && (
              <Flex flexDirection="column" css={{ width: "40%" }}>
                <>
                  <SettingCard
                    glStartDate={glStartDateCapitalCalls}
                    setGlStartDate={setGlStartDateCapitalCalls}
                    setUseCustomStartDate={setUseCustomStartDateCapitalCalls}
                    useCustomStartDate={useCustomStartDateCapitalCalls}
                    entityName={entityName}
                    arenaName={arenaName}
                    batchPeriod={null}
                    setBatchPeriod={null}
                    transactionCategoryTitle="Capital calls"
                    showReportingInterval={false}
                    setChecked={setCheckedCapitalCalls}
                    checked={checkedCapitalCalls}
                    currentGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                        ?.glStartDate
                        ? new Date(
                            parseISO(
                              entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                                ?.glStartDate,
                            ),
                          )
                        : null
                    }
                    currentUseCustomGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                        ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                            .active
                          ? entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                              ?.glStartDate
                            ? true
                            : false
                          : true
                        : true
                    }
                    currentBatchPeriod={null}
                  />
                  <br></br>
                </>
                <>
                  <SettingCard
                    glStartDate={glStartDateRedemptions}
                    setGlStartDate={setGlStartDateRedemptions}
                    setUseCustomStartDate={setUseCustomStartDateRedemptions}
                    useCustomStartDate={useCustomStartDateRedemptions}
                    entityName={entityName}
                    arenaName={arenaName}
                    batchPeriod={null}
                    setBatchPeriod={null}
                    transactionCategoryTitle={capitalActivityLabel}
                    showReportingInterval={false}
                    setChecked={setCheckedRedemptions}
                    checked={checkedRedemptions}
                    currentGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings
                        ?.glStartDate
                        ? new Date(
                            parseISO(
                              entityGlSyncSettingsData?.entityGLSyncSettings
                                .capitalTransactionSettings?.glStartDate,
                            ),
                          )
                        : null
                    }
                    currentUseCustomGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.capitalTransactionSettings
                        ? entityGlSyncSettingsData?.entityGLSyncSettings
                            .capitalTransactionSettings.active
                          ? entityGlSyncSettingsData?.entityGLSyncSettings
                              .capitalTransactionSettings?.glStartDate
                            ? true
                            : false
                          : true
                        : true
                    }
                    currentBatchPeriod={null}
                  />
                  <br></br>
                </>
                <>
                  <SettingCard
                    glStartDate={glStartDateDistributions}
                    setGlStartDate={setGlStartDateDistributions}
                    setUseCustomStartDate={setUseCustomStartDateDistributions}
                    useCustomStartDate={useCustomStartDateDistributions}
                    entityName={entityName}
                    arenaName={arenaName}
                    batchPeriod={null}
                    setBatchPeriod={null}
                    transactionCategoryTitle="Distributions"
                    showReportingInterval={false}
                    setChecked={setCheckedDistributions}
                    checked={checkedDistributions}
                    currentGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                        ?.glStartDate
                        ? new Date(
                            parseISO(
                              entityGlSyncSettingsData?.entityGLSyncSettings
                                .distributionSettings?.glStartDate,
                            ),
                          )
                        : null
                    }
                    currentUseCustomGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                        ? entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                            .active
                          ? entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                              ?.glStartDate
                            ? true
                            : false
                          : true
                        : true
                    }
                    currentBatchPeriod={null}
                  />
                  <br></br>
                </>
                <SettingCard
                  glStartDate={glStartDateNetIncome}
                  setGlStartDate={setGlStartDateNetIncome}
                  setUseCustomStartDate={setUseCustomStartDateNetIncome}
                  useCustomStartDate={useCustomStartDateNetIncome}
                  entityName={entityName}
                  arenaName={arenaName}
                  batchPeriod={batchPeriodNetIncome}
                  setBatchPeriod={setBatchPeriodNetIncome}
                  transactionCategoryTitle="Net income"
                  showReportingInterval={true}
                  setChecked={setCheckedNetIncome}
                  checked={checkedNetIncome}
                  currentGlStartDate={
                    entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                      ?.glStartDate
                      ? new Date(
                          parseISO(
                            entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                              ?.glStartDate,
                          ),
                        )
                      : null
                  }
                  currentUseCustomGlStartDate={
                    entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                      ? entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings.active
                        ? entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                            ?.glStartDate
                          ? true
                          : false
                        : true
                      : true
                  }
                  currentBatchPeriod={
                    entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                      ?.batchPeriod ?? BatchPeriod.Quarterly
                  }
                />

                <>
                  <br></br>
                  <SettingCard
                    glStartDate={glStartDateTransfers}
                    setGlStartDate={setGlStartDateTransfers}
                    setUseCustomStartDate={setUseCustomStartDateTransfers}
                    useCustomStartDate={useCustomStartDateTransfers}
                    entityName={entityName}
                    arenaName={arenaName}
                    batchPeriod={null}
                    setBatchPeriod={null}
                    transactionCategoryTitle="Transfers"
                    showReportingInterval={false}
                    setChecked={setCheckedTransfers}
                    checked={checkedTransfers}
                    currentGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings
                        ?.glStartDate
                        ? new Date(
                            parseISO(
                              entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings
                                ?.glStartDate,
                            ),
                          )
                        : null
                    }
                    currentUseCustomGlStartDate={
                      entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings
                        ? entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings.active
                          ? entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings
                              ?.glStartDate
                            ? true
                            : false
                          : true
                        : true
                    }
                    currentBatchPeriod={null}
                    customHelpText={`
                        To prevent duplicate transaction entries, select a GL integration start date
                        that comes after the date of the last recorded transfer event with offsetting
                        transactions in Juniper Square (e.g. If the last recorded transfer event with
                        offsetting transactions in ${arenaName}'s workspace was on 02/28/2025, choose
                        03/01/2025, as your GL integration start date)
                      `}
                  />
                </>

                <Flex css={{ marginTop: SPACING.LARGE }} justifyContent="flex-end">
                  <Button onClick={() => nextStep()} disabled={mutationLoading}>
                    Review settings
                  </Button>
                </Flex>
              </Flex>
            )}
            {currentStep == STEP_NAMES.reviewSettings && (
              <Flex flexDirection="column" css={{ minWidth: summaryWidth }}>
                {errorMessage && (
                  <>
                    <Banner variant="danger" title="Error" description={errorMessage} />
                    <br></br>
                  </>
                )}
                <Card
                  title={"Integration settings summary"}
                  subTitle={"Updates in settings will only affect new transactions syncs."}
                >
                  <Grid
                    className="GridWrapper"
                    gridOptions={gridOptions}
                    rowData={rowData}
                    columnDefs={columnDefs}
                  />
                </Card>
                <Flex css={{ marginTop: SPACING.LARGE }} justifyContent="flex-end">
                  <Button
                    css={{ marginRight: SPACING.MEDIUM }}
                    variant="neutral"
                    onClick={() => onBack()}
                  >
                    Back
                  </Button>
                  <Button onClick={() => onSubmit()} disabled={mutationLoading}>
                    Save integration settings
                  </Button>
                </Flex>
              </Flex>
            )}
          </>
        )}
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
